import Footer from "../../components/main/Footer"
import Basket from "../../components/organisms/Basket"
import { Header } from "../../components/organisms/Header"
import { Topbar } from "../../components/organisms/Topbar"
import ApiService from "../../core/service/ApiService"
import {LOCAL_STORAGE_TUNNEL_DATA_KEY, initPdiComponent, sendToLogServer} from "../../core/service/BusinessLogicService"
import { loc } from "../../core/utils"
import React, { useEffect, useRef, useState } from "react"
import { Col, Container, ProgressBar, Row } from "react-bootstrap"

const APZ_PROGRESS_CONFIG = {
  startPercent: 10,
  settlingPercent: 92,
  maximumPercent: 97,
  expectedDurationMs: 25000
}

function createHeaders(tunnelData) {
  const token = tunnelData?.access_token
  return {
    Authorization: `Bearer ${token}`,
    Accept: "*/*",
    "Content-Type": "application/json"
  }
}

function getProgressConfig() {
  return APZ_PROGRESS_CONFIG
}

function getSmoothedProgress(elapsedMs, progressConfig) {
  const { startPercent, settlingPercent, maximumPercent, expectedDurationMs } = progressConfig

  if (elapsedMs <= expectedDurationMs) {
    const ratio = Math.min(elapsedMs / expectedDurationMs, 1)
    const easedRatio = 1 - Math.pow(1 - ratio, 1.35)
    return startPercent + (settlingPercent - startPercent) * easedRatio
  }

  const overflowMs = elapsedMs - expectedDurationMs
  const tailDurationMs = Math.max(expectedDurationMs * 0.6, 2000)
  const tailRatio = 1 - Math.exp(-overflowMs / tailDurationMs)
  return Math.min(maximumPercent, settlingPercent + (maximumPercent - settlingPercent) * tailRatio)
}

export default function AplazameConfirmationPage({ tunnelData, history, setTunnelData, onSetStep }) {
  const [progress, setProgress] = useState(getProgressConfig().startPercent)
  const onSetStepRef = useRef(onSetStep)

  useEffect(() => {
    onSetStepRef.current = onSetStep
  }, [onSetStep])

  useEffect(() => {
    initPdiComponent("AplazameConfirmationPage")

    let isMounted = true
    const progressConfig = getProgressConfig()
    let animationFrameId = null
    let redirectTimeoutId = null
    const startedAt = window.performance?.now?.() ?? Date.now()

    const stopProgressAnimation = () => {
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId)
        animationFrameId = null
      }
    }

    const updateProgress = () => {
      if (!isMounted) return

      const now = window.performance?.now?.() ?? Date.now()
      const nextProgress = getSmoothedProgress(now - startedAt, progressConfig)
      setProgress((currentProgress) => Math.max(currentProgress, nextProgress))
      animationFrameId = window.requestAnimationFrame(updateProgress)
    }

    const redirectTo = (route, delayMs = 150) => {
      stopProgressAnimation()
      setProgress(100)
      redirectTimeoutId = window.setTimeout(() => {
        if (isMounted) {
          history.push(route)
        }
      }, delayMs)
    }

    updateProgress()

    const handleScoreKo = () => {
      const onSetStepFn = onSetStepRef.current
      tunnelData.scoreKO = true
      tunnelData.rejectedProvision = true;
      if (typeof onSetStepFn === "function") {
        onSetStepFn()
      }
    }

    const runAplazameConfirmation = async () => {
      try {
        const transactionId = tunnelData?.data?.order?.id
        const headers = createHeaders(tunnelData)
        const confirmationResponse = await ApiService.postForEvollisApi(
          `${tunnelData.apiUrl}api/apz/transactions/${transactionId}/process-client-file-accepted-awaiting-funding`,
          { netheosUuid: tunnelData?.urlUUIDToken || null },
          { headers }
        )

        const confirmation = {
          status: confirmationResponse?.status ?? null,
          step: confirmationResponse?.step ?? null
        }
        sendToLogServer("INFO", "Aplazame confirmation page : confirmation received for transaction : " + tunnelData.opl + " : " + JSON.stringify(confirmation));
        const statusName = confirmation?.status?.name ?? null

        let nextStepId = tunnelData?.step
        let nextRoute = null

        if (statusName === null) {
          tunnelData.aplazameConfirmation = confirmation
          if (isMounted) {
            handleScoreKo()
          }
          return
        } else if (statusName === "payment-method-rejected" || statusName === "downpayment-auth") {
          nextStepId = "stepApzCheckout"
          nextRoute = "/digital-journey/apz-checkout"
        } else if (statusName === "accepted" || statusName === "ok") {
          const nextStep = tunnelData?.breadcrumbs?.find((step) => step.id === "stepFinal")
          nextStepId = nextStep?.id || nextStepId
          nextRoute = nextStep?.route
        } else if(statusName === "ko"){
          handleScoreKo()
        }

        const finalTunnelData = {
          ...tunnelData,
          aplazameConfirmation: confirmation,
          step: nextStepId
        }

        window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(finalTunnelData))
        if (typeof setTunnelData === "function") {
          setTunnelData(finalTunnelData)
        }

        if (isMounted) {
          redirectTo(nextRoute || "/digital-journey/score-ko")
        }
      } catch (error) {
        if (isMounted) {
          handleScoreKo()
        }
      } finally {
        stopProgressAnimation()
      }
    }

    runAplazameConfirmation()

    return () => {
      isMounted = false
      stopProgressAnimation()
      if (redirectTimeoutId != null) {
        window.clearTimeout(redirectTimeoutId)
      }
    }
  }, [history, setTunnelData, tunnelData])

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        <Col className="main-block" lg={8}>
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />
            <Col sm={12}>
              <Container style={{ minHeight: "45vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: "100%", maxWidth: "420px", textAlign: "center" }}>
                  <p
                    style={{ marginBottom: "12px", fontSize: "1.25rem", lineHeight: 1.5 }}
                  >{loc`Your transaction is being finalized.`}</p>
                  <ProgressBar animated now={progress} />
                </div>
              </Container>
            </Col>
            <Col sm={12}>
              <Container>
                <Footer tunnelData={tunnelData} />
              </Container>
            </Col>
          </Row>
        </Col>
        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <Basket tunnelData={tunnelData} />
        </Col>
      </Row>
    </Container>
  )
}
